import PropTypes from "prop-types"
import React, { useContext } from "react"
import { ExportAction } from "src/actions/contracts"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import DataTableV2 from "src/components/DataTableV2"

import {
  formatCurrencyFromMinorUnits,
  inchesToFeet,
} from "src/utils/UnitConversion"

import { ManageContractsContext } from ".."

const COLUMNS = [
  {
    key: "status",
    header: "Status",
    cardRole: "hidden",
    render: (row) => (
      <Badge color={statusToken(row.status)} text={row.status} />
    ),
  },
  {
    key: "customerName",
    header: "Customer",
    sortKey: "captain_name",
    width: "150px",
    sortable: true,
    cardRole: "title",
    cardOrder: 0,
    render: (row) => (
      <div className="truncate font-semibold">{row.customerName}</div>
    ),
    cardRender: (row) => (
      <div className="space-y-1">
        <div className="flex w-full flex-wrap items-center justify-between gap-1">
          <div>{row.customerName}</div>
          <Badge color={statusToken(row.status)} text={row.status} />
        </div>
        {row.contactBoat && (
          <div className="flex items-center gap-2 text-sm font-normal">
            <BoatPreviewLink boat={row.contactBoat} />
            <span className="text-xs text-gray-600">
              LOA: {inchesToFeet(row.contactBoat.lengthOverall)}&apos;
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm font-normal text-gray-700">
          <span>
            {row.startOn}
            {row.endOn && ` – ${row.endOn}`}
          </span>
          <span className="text-gray-400">&middot;</span>
          <span className="text-gray-500">#{row.contractId}</span>
        </div>
      </div>
    ),
  },
  {
    key: "contactBoatName",
    header: "Boat",
    sortKey: "contact_boat",
    width: "150px",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat ? <BoatPreviewLink boat={row.contactBoat} /> : null,
  },
  {
    key: "startOn",
    header: "Start",
    width: "110px",
    sortKey: "start_date",
    sortable: true,
    cardRole: "hidden",
  },
  {
    key: "endOn",
    header: "End",
    width: "110px",
    sortKey: "end_date",
    sortable: true,
    cardRole: "hidden",
  },
  {
    key: "signedOn",
    header: "Signed",
    width: "110px",
    sortKey: "completed_at",
    sortable: true,
  },
  {
    key: "createdOn",
    header: "Created",
    width: "110px",
    sortKey: "created_at",
    sortable: true,
  },
  {
    key: "contractGroupName",
    header: "Group",
    sortKey: "contract_group_name",
    sortable: true,
  },
  {
    key: "lengthOverall",
    header: "LOA",
    width: "75px",
    sortKey: "length_overall",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.lengthOverall
        ? `${inchesToFeet(row.contactBoat.lengthOverall)}'`
        : null,
  },
  {
    key: "spaceType",
    width: "110px",
    header: "Space Type",
    sortKey: "space_type",
    sortable: true,
  },
  {
    key: "revenueCategoryName",
    header: "Revenue Category",
  },
  {
    key: "autopay",
    width: "110px",
    header: "Autopay",
    cardRole: "hidden",
  },
  {
    key: "displayPriceCents",
    width: "110px",
    header: "Price",
    render: (row) =>
      row.displayPriceCents != null ? (
        <div className="inline">
          <span>
            {formatCurrencyFromMinorUnits({ amount: row.displayPriceCents })}
          </span>
          {row.autopay && (
            <span className="ml-1 text-gray-600">Autopay: {row.autopay}</span>
          )}
        </div>
      ) : null,
  },
  {
    key: "encodedId",
    width: "110px",
    header: "ID",
    sortKey: "created_at",
    sortable: true,
    cardRole: "hidden",
  },
]

const ContractsTable = ({ table, onPrint, onExport }) => {
  const { archivedGroupsPagePath } = useContext(ManageContractsContext)

  const actions = onExport ? [ExportAction({ onExport })] : []

  if (table.isError) {
    return (
      <div className="text-muted bg-white p-5 text-center">
        <h3 className="mb-5">{table.error?.message}</h3>
      </div>
    )
  }

  return (
    <DataTableV2
      name="contracts"
      columns={COLUMNS}
      data={table.records}
      actions={actions}
      rowConfig={{ getViewUrl: (row) => row.linkPath }}
      rowCount={10}
      maxHeight="none"
      emptyState={
        <div className="flex flex-col text-center">
          <span className="mb-2 text-xl font-bold">No matching contracts</span>
          <span className="text-gray-600">
            We didn&apos;t find any contracts matching your search criteria. Try
            adjusting your filters. Contracts from archived groups can be found
            by clicking on the group from the{" "}
            <a className="font-semibold" href={archivedGroupsPagePath}>
              archived groups page
            </a>
          </span>
        </div>
      }
      onPrint={onPrint}
      {...table.tableProps}
    />
  )
}

ContractsTable.propTypes = {
  table: PropTypes.object.isRequired,
  onPrint: PropTypes.func,
  onExport: PropTypes.func,
}

export default ContractsTable
